import React, { useState } from 'react';

interface QuantityControlsProps {
  itemId: number;
  quantity: number;
  onChange?: (itemId: number, newQuantity: number) => void;
  compact?: boolean;
  size?: 'small' | 'medium';
  vertical?: boolean; // Vertical layout: [+] / n / [-]
}

export const QuantityControls: React.FC<QuantityControlsProps> = ({
  itemId,
  quantity,
  onChange,
  compact = false,
  size = 'medium',
  vertical = false,
}) => {
  // Use medium sizing for usability
  const buttonSize = 28;
  const gap = 4;
  const fontSize = 14;
  const minNumWidth = 24;
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(quantity.toString());

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onChange) {
      onChange(itemId, quantity + 1);
    }
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onChange && quantity > -1) {
      onChange(itemId, quantity - 1);
    }
  };

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setTempValue(quantity.toString());
  };

  const finishEditing = () => {
    const newQty = parseInt(tempValue) || 0;
    const finalQty = Math.max(-1, newQty);
    if (onChange) {
      onChange(itemId, finalQty);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      finishEditing();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setTempValue(quantity.toString());
    }
  };

  // Vertical layout: + and - with circles - for right edge of card
  // Gap adjusts based on size to match thumbnail height (small=96px, medium=110px)
  const verticalGap = size === 'medium' ? '12px' : '4px';
  
  if (vertical) {
    return (
      <div className="quantity-controls-vertical" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: verticalGap }}>
        <button
          onClick={handleIncrement}
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            border: '1.5px solid var(--color-border-strong)',
            background: 'var(--color-bg-primary)',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-muted)',
            padding: 0,
            lineHeight: 1,
          }}
        >
          +
        </button>
        
        {isEditing ? (
          <input
            type="number"
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onBlur={finishEditing}
            onKeyDown={handleKeyDown}
            autoFocus
            style={{
              width: '36px',
              textAlign: 'center',
              padding: '2px',
              border: '1px solid #3b82f6',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
        ) : (
          <span
            onClick={startEditing}
            style={{
              minWidth: '24px',
              textAlign: 'center',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '16px',
              padding: '4px 0',
              color: 'var(--color-text-secondary)',
            }}
          >
            {quantity}
          </span>
        )}
        
        <button
          onClick={handleDecrement}
          disabled={quantity <= -1}
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            border: '1.5px solid var(--color-border-strong)',
            background: 'var(--color-bg-primary)',
            cursor: quantity <= -1 ? 'not-allowed' : 'pointer',
            fontSize: '18px',
            fontWeight: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: quantity <= -1 ? 'var(--color-border-strong)' : 'var(--color-text-muted)',
            padding: 0,
            lineHeight: 1,
          }}
        >
          −
        </button>
      </div>
    );
  }

  if (compact) {
    // Compact horizontal layout for list view
    return (
      <div className="quantity-controls-compact" style={{ display: 'flex', alignItems: 'center', gap: `${gap}px` }}>
        <button
          onClick={handleDecrement}
          disabled={quantity <= -1}
          style={{
            width: `${buttonSize}px`,
            height: `${buttonSize}px`,
            borderRadius: '4px',
            border: '1px solid var(--color-border-strong)',
            background: 'var(--color-bg-primary)',
            cursor: quantity <= -1 ? 'not-allowed' : 'pointer',
            fontSize: `${fontSize}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          −
        </button>
        
        {isEditing ? (
          <input
            type="number"
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onBlur={finishEditing}
            onKeyDown={handleKeyDown}
            autoFocus
            style={{
              width: size === 'small' ? '36px' : '50px',
              textAlign: 'center',
              padding: size === 'small' ? '2px' : '4px',
              border: '1px solid #3b82f6',
              borderRadius: '4px',
              fontSize: `${fontSize}px`,
            }}
          />
        ) : (
          <span
            onClick={startEditing}
            style={{
              minWidth: `${minNumWidth}px`,
              textAlign: 'center',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: `${fontSize}px`,
            }}
          >
            {quantity}
          </span>
        )}
        
        <button
          onClick={handleIncrement}
          style={{
            width: `${buttonSize}px`,
            height: `${buttonSize}px`,
            borderRadius: '4px',
            border: '1px solid var(--color-border-strong)',
            background: 'var(--color-bg-primary)',
            cursor: 'pointer',
            fontSize: `${fontSize}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          +
        </button>
      </div>
    );
  }

  // Full layout for card view
  return (
    <div className="quantity-controls" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '12px' }}>
      <button
        onClick={handleDecrement}
        disabled={quantity <= -1}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          border: '2px solid var(--color-border-strong)',
          background: 'var(--color-bg-primary)',
          cursor: quantity <= -1 ? 'not-allowed' : 'pointer',
          fontSize: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
        }}
      >
        −
      </button>
      
      {isEditing ? (
        <input
          type="number"
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onBlur={finishEditing}
          onKeyDown={handleKeyDown}
          autoFocus
          style={{
            width: '60px',
            textAlign: 'center',
            padding: '8px',
            border: '2px solid #3b82f6',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 600,
          }}
        />
      ) : (
        <span
          onClick={startEditing}
          style={{
            minWidth: '40px',
            textAlign: 'center',
            fontSize: '24px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {quantity}
        </span>
      )}
      
      <button
        onClick={handleIncrement}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          border: '2px solid var(--color-border-strong)',
          background: 'var(--color-bg-primary)',
          cursor: 'pointer',
          fontSize: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
        }}
      >
        +
      </button>
    </div>
  );
};
